import { useCallback, useEffect, useState } from "react";

import { downloadText, generateKeyAndCsr } from "../lib/csr";
import { CopyButton } from "./CopyButton";

const SECRET_KEY = "sunlit-ca-admin-secret";
const SOON_MS = 30 * 24 * 3600 * 1000;

interface ProfileMeta {
  ca: string;
  max_validity_days: number;
  san_policy: string;
  description: string;
}

interface CertRow {
  serial_hex: string;
  ca_slug: string;
  profile: string;
  subject: string;
  status: string;
  not_after: string;
  created_at: string;
  revocation_reason: string | null;
  note: string | null;
}

interface TokenRow {
  id: number;
  token_prefix: string;
  profile: string;
  cn_pattern: string | null;
  max_uses: number;
  use_count: number;
  expires_at: string;
  note: string | null;
  revoked_at: string | null;
}

interface AuditRow {
  id: number;
  ts: string;
  actor: string;
  action: string;
  target: string | null;
  detail_json: string | null;
}

const REVOKE_REASONS = [
  "unspecified", "keyCompromise", "superseded",
  "affiliationChanged", "cessationOfOperation",
];

function StatusPill({ status, reason }: { status: string; reason?: string | null }) {
  const cls = status === "active" ? "pill pill-ok" : "pill pill-bad";
  return (
    <span className={cls}>
      <span className="pill-dot" aria-hidden="true" />
      {status}
      {reason ? ` · ${reason}` : ""}
    </span>
  );
}

function SkeletonRows({ cols, rows = 3 }: { cols: number; rows?: number }) {
  return (
    <tbody aria-hidden="true">
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <td key={c}>
              <span className="skeleton" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

function EmptyRow({ cols, children }: { cols: number; children: React.ReactNode }) {
  return (
    <tbody>
      <tr>
        <td colSpan={cols} className="empty-cell">
          {children}
        </td>
      </tr>
    </tbody>
  );
}

function expiringSoon(notAfter: string): boolean {
  return new Date(notAfter).getTime() - Date.now() < SOON_MS;
}

function useAdminApi(secret: string, onAuthFail: () => void) {
  return useCallback(
    async (path: string, init?: RequestInit) => {
      const resp = await fetch(path, {
        ...init,
        headers: {
          ...(init?.headers ?? {}),
          Authorization: `Bearer ${secret}`,
          ...(init?.body ? { "Content-Type": "application/json" } : {}),
        },
      });
      if (resp.status === 401) {
        onAuthFail();
        throw new Error("admin secret rejected");
      }
      if (!resp.ok) {
        let detail = resp.statusText;
        try {
          detail = (await resp.json()).detail ?? detail;
        } catch {
          /* keep statusText */
        }
        throw new Error(detail);
      }
      return resp.json();
    },
    [secret, onAuthFail],
  );
}

export function CaAdminPage() {
  const [secret, setSecret] = useState(
    () => sessionStorage.getItem(SECRET_KEY) ?? "",
  );
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lock = useCallback(() => {
    sessionStorage.removeItem(SECRET_KEY);
    setSecret("");
    setUnlocked(false);
  }, []);
  const api = useAdminApi(secret, lock);

  useEffect(() => {
    if (!secret || unlocked) return;
    setChecking(true);
    api("/ca/certs?status=active")
      .then(() => setUnlocked(true))
      .catch((e: Error) => setError(e.message))
      .finally(() => setChecking(false));
  }, [secret, unlocked, api]);

  if (!unlocked) {
    return (
      <section className="panel admin-unlock">
        <p className="eyebrow">Sunlit Bytes CA</p>
        <h3>Administration</h3>
        <p className="muted">
          Enter the CA admin secret. It stays in this tab's session storage
          and is only ever sent to {window.location.host}.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const value = new FormData(e.currentTarget).get("secret");
            const s = String(value ?? "").trim();
            if (!s) return;
            sessionStorage.setItem(SECRET_KEY, s);
            setError(null);
            setSecret(s);
          }}
        >
          <label className="visually-hidden" htmlFor="admin-secret">
            CA admin secret
          </label>
          <input
            id="admin-secret"
            type="password"
            name="secret"
            placeholder="CA admin secret"
            autoComplete="off"
            autoFocus
            className="mono"
          />
          <button type="submit" disabled={checking}>
            {checking ? "Checking…" : "Unlock"}
          </button>
        </form>
        {error && (
          <p className="admin-error" role="alert">
            {error}
          </p>
        )}
      </section>
    );
  }

  return (
    <div className="ca-admin">
      <section className="panel admin-bar">
        <div>
          <p className="eyebrow">Sunlit Bytes CA</p>
          <h3>Administration</h3>
        </div>
        <button className="ghost small" onClick={lock}>
          Lock
        </button>
      </section>
      <IssuePanel api={api} />
      <TokensPanel api={api} />
      <CertsPanel api={api} />
      <AuditPanel api={api} />
    </div>
  );
}

type Api = ReturnType<typeof useAdminApi>;

function IssuePanel({ api }: { api: Api }) {
  const [profiles, setProfiles] = useState<Record<string, ProfileMeta>>({});
  const [profile, setProfile] = useState("");
  const [cn, setCn] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    serial: string;
    subject: string;
    not_after: string;
    cert_pem: string;
    chain_pem: string;
    privateKeyPem: string;
  } | null>(null);

  useEffect(() => {
    fetch("/ca/profiles")
      .then((r) => r.json())
      .then((p) => {
        setProfiles(p);
        setProfile(Object.keys(p)[0] ?? "");
      });
  }, []);

  const meta = profiles[profile];
  const needsEmail = meta?.san_policy === "rfc822_required";
  const allowsEmail = needsEmail || meta?.san_policy === "rfc822_optional";
  const isDevice = profile === "device";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { csrPem, privateKeyPem } = await generateKeyAndCsr(
        cn.trim(),
        allowsEmail && email.trim() ? email.trim() : undefined,
      );
      const data = await api("/ca/issue", {
        method: "POST",
        body: JSON.stringify({ profile, csr_pem: csrPem }),
      });
      setResult({ ...data, privateKeyPem });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel">
      <h3>Issue a certificate</h3>
      <p className="muted">
        The private key is generated in this browser and never leaves it —
        download it immediately; the CA cannot recover it.
      </p>
      <form className="admin-form" onSubmit={submit}>
        <label>
          Profile
          <select value={profile} onChange={(e) => setProfile(e.target.value)}>
            {Object.entries(profiles).map(([name, p]) => (
              <option key={name} value={name}>
                {name} — {p.description}
              </option>
            ))}
          </select>
          {meta && (
            <span className="helper">
              Issued by {meta.ca} · up to {meta.max_validity_days} days
            </span>
          )}
        </label>
        <label>
          Common name
          <input
            value={cn}
            onChange={(e) => setCn(e.target.value)}
            placeholder={isDevice ? "press-01" : "Name of the person or thing"}
            required
          />
          {isDevice && (
            <span className="helper">
              Device id: letters, digits, - and _ (becomes
              <code className="mono"> …devices.sunlitbytes.com</code>)
            </span>
          )}
        </label>
        {allowsEmail && (
          <label>
            Email {needsEmail ? "(required)" : "(optional)"}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required={needsEmail}
              placeholder="who@sunlitbytes.com"
            />
            {needsEmail && (
              <span className="helper">
                Bound into the certificate as its rfc822 identity
              </span>
            )}
          </label>
        )}
        <button type="submit" disabled={busy || !cn.trim()}>
          {busy ? "Issuing…" : "Generate key & issue"}
        </button>
      </form>
      {error && (
        <p className="admin-error" role="alert">
          {error}
        </p>
      )}
      {result && (
        <div className="issue-result" role="status">
          <p>
            Issued <code className="mono">{result.serial.slice(0, 16)}…</code>{" "}
            for <strong>{result.subject}</strong> (valid to{" "}
            {result.not_after.slice(0, 10)}).
          </p>
          <p className="key-warning">
            ⚠ Download the private key now — it exists only in this tab.
          </p>
          <div className="download-row">
            <button
              onClick={() => downloadText("key.pem", result.privateKeyPem)}
            >
              Download private key
            </button>
            <button
              className="ghost"
              onClick={() => downloadText("cert.pem", result.cert_pem)}
            >
              Certificate
            </button>
            <button
              className="ghost"
              onClick={() => downloadText("chain.pem", result.chain_pem)}
            >
              Chain
            </button>
            <button
              className="ghost"
              onClick={() =>
                downloadText(
                  "bundle.pem",
                  result.privateKeyPem + result.cert_pem + result.chain_pem,
                )
              }
            >
              Everything (bundle.pem)
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function TokensPanel({ api }: { api: Api }) {
  const [tokens, setTokens] = useState<TokenRow[] | null>(null);
  const [profiles, setProfiles] = useState<string[]>([]);
  const [profile, setProfile] = useState("user-mtls");
  const [ttlHours, setTtlHours] = useState(24);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [minted, setMinted] = useState<{ token: string; profile: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    api("/ca/enroll-tokens")
      .then((d) => setTokens(d.tokens))
      .catch((e: Error) => setError(e.message));
  }, [api]);

  useEffect(() => {
    refresh();
    fetch("/ca/profiles")
      .then((r) => r.json())
      .then((p) => setProfiles(Object.keys(p)));
  }, [refresh]);

  const enrollLink = minted
    ? `${window.location.origin}/#/enroll?token=${minted.token}`
    : "";
  const mailto = minted
    ? `mailto:?subject=${encodeURIComponent("Your Sunlit Bytes certificate")}&body=${encodeURIComponent(
        `Open this link to generate your ${minted.profile} certificate. ` +
          `Your private key is created in your own browser and never leaves it.\n\n${enrollLink}\n\n` +
          `The link is single-use and expires soon.`,
      )}`
    : "";

  return (
    <section className="panel">
      <h3>Send a certificate to someone</h3>
      <p className="muted">
        Mint a single-use enrollment link. The recipient's browser generates
        their private key; you never handle it.
      </p>
      <form
        className="admin-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          setMinted(null);
          setBusy(true);
          try {
            const data = await api("/ca/enroll-tokens", {
              method: "POST",
              body: JSON.stringify({
                profile,
                ttl_seconds: ttlHours * 3600,
                note: note || null,
              }),
            });
            setMinted({ token: data.token, profile });
            refresh();
          } catch (err) {
            setError((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <label>
          Profile
          <select value={profile} onChange={(e) => setProfile(e.target.value)}>
            {profiles.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>
        </label>
        <label>
          Expires after
          <select
            value={ttlHours}
            onChange={(e) => setTtlHours(Number(e.target.value))}
          >
            <option value={1}>1 hour</option>
            <option value={24}>24 hours</option>
            <option value={168}>7 days</option>
          </select>
        </label>
        <label>
          Note
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Who is this for?"
          />
        </label>
        <button type="submit" disabled={busy}>
          {busy ? "Creating…" : "Create enrollment link"}
        </button>
      </form>
      {minted && (
        <div className="issue-result" role="status">
          <p className="key-warning">⚠ Shown once — copy or send it now.</p>
          <p className="mono token-link">
            {enrollLink} <CopyButton text={enrollLink} />
          </p>
          <div className="download-row">
            <a className="button-link" href={mailto}>
              <button className="ghost">Email it…</button>
            </a>
          </div>
        </div>
      )}
      {error && (
        <p className="admin-error" role="alert">
          {error}
        </p>
      )}
      <table className="admin-table">
        <thead>
          <tr>
            <th>token</th><th>profile</th><th>uses</th><th>expires</th>
            <th>note</th><th></th>
          </tr>
        </thead>
        {tokens === null ? (
          <SkeletonRows cols={6} />
        ) : tokens.length === 0 ? (
          <EmptyRow cols={6}>
            No enrollment links yet — mint one above to invite someone.
          </EmptyRow>
        ) : (
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id} className={t.revoked_at ? "row-revoked" : ""}>
                <td className="mono">{t.token_prefix}…</td>
                <td>{t.profile}</td>
                <td>
                  {t.use_count}/{t.max_uses}
                </td>
                <td>{t.expires_at.slice(0, 16).replace("T", " ")}</td>
                <td>{t.note ?? ""}</td>
                <td>
                  {!t.revoked_at && t.use_count < t.max_uses && (
                    <button
                      className="ghost small danger"
                      onClick={async () => {
                        await api(`/ca/enroll-tokens/${t.id}`, {
                          method: "DELETE",
                        });
                        refresh();
                      }}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </section>
  );
}

function CertsPanel({ api }: { api: Api }) {
  const [certs, setCerts] = useState<CertRow[] | null>(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  const [reason, setReason] = useState("unspecified");

  const refresh = useCallback(() => {
    const qs = status ? `?status=${status}` : "";
    api(`/ca/certs${qs}`)
      .then((d) => setCerts(d.certificates))
      .catch((e: Error) => setError(e.message));
  }, [api, status]);

  useEffect(refresh, [refresh]);

  return (
    <section className="panel">
      <h3>Certificates</h3>
      <div className="admin-form-inline">
        <label className="visually-hidden" htmlFor="cert-status-filter">
          Filter by status
        </label>
        <select
          id="cert-status-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">all statuses</option>
          <option value="active">active</option>
          <option value="revoked">revoked</option>
        </select>
        <button className="ghost small" onClick={refresh}>
          Refresh
        </button>
      </div>
      {error && (
        <p className="admin-error" role="alert">
          {error}
        </p>
      )}
      <table className="admin-table">
        <thead>
          <tr>
            <th>serial</th><th>profile</th><th>subject</th>
            <th>expires</th><th>status</th><th></th>
          </tr>
        </thead>
        {certs === null ? (
          <SkeletonRows cols={6} />
        ) : certs.length === 0 ? (
          <EmptyRow cols={6}>
            Nothing here{status ? ` with status “${status}”` : ""} — issue a
            certificate above to get started.
          </EmptyRow>
        ) : (
          <tbody>
            {certs.map((c) => (
              <tr
                key={c.serial_hex}
                className={c.status === "revoked" ? "row-revoked" : ""}
              >
                <td className="mono">
                  <a href={`/ca/certs/${c.serial_hex}.pem`}>
                    {c.serial_hex.slice(0, 12)}…
                  </a>
                </td>
                <td>{c.profile}</td>
                <td>{c.subject.replace(/,O=Sunlit Bytes,C=US$/, "")}</td>
                <td
                  className={
                    c.status === "active" && expiringSoon(c.not_after)
                      ? "expiring"
                      : ""
                  }
                >
                  {c.not_after.slice(0, 10)}
                  {c.status === "active" && expiringSoon(c.not_after)
                    ? " ⚠"
                    : ""}
                </td>
                <td>
                  <StatusPill status={c.status} reason={c.revocation_reason} />
                </td>
                <td>
                  {c.status === "active" &&
                    (confirmRevoke === c.serial_hex ? (
                      <span className="revoke-confirm">
                        <label
                          className="visually-hidden"
                          htmlFor={`reason-${c.serial_hex}`}
                        >
                          Revocation reason
                        </label>
                        <select
                          id={`reason-${c.serial_hex}`}
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                        >
                          {REVOKE_REASONS.map((r) => (
                            <option key={r}>{r}</option>
                          ))}
                        </select>
                        <button
                          className="small danger-solid"
                          onClick={async () => {
                            try {
                              await api(`/ca/certs/${c.serial_hex}/revoke`, {
                                method: "POST",
                                body: JSON.stringify({ reason }),
                              });
                              setConfirmRevoke(null);
                              refresh();
                            } catch (err) {
                              setError((err as Error).message);
                            }
                          }}
                        >
                          Confirm revoke
                        </button>
                        <button
                          className="ghost small"
                          onClick={() => setConfirmRevoke(null)}
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        className="ghost small danger"
                        onClick={() => setConfirmRevoke(c.serial_hex)}
                      >
                        Revoke
                      </button>
                    ))}
                </td>
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </section>
  );
}

function AuditPanel({ api }: { api: Api }) {
  const [entries, setEntries] = useState<AuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    api("/ca/audit?limit=100")
      .then((d) => setEntries(d.entries))
      .catch((e: Error) => setError(e.message));
  }, [api]);

  useEffect(refresh, [refresh]);

  return (
    <section className="panel">
      <div className="admin-form-inline">
        <h3>Audit log</h3>
        <button className="ghost small" onClick={refresh}>
          Refresh
        </button>
      </div>
      {error && (
        <p className="admin-error" role="alert">
          {error}
        </p>
      )}
      <table className="admin-table">
        <thead>
          <tr>
            <th>time (UTC)</th><th>actor</th><th>action</th><th>target</th>
            <th>detail</th>
          </tr>
        </thead>
        {entries === null ? (
          <SkeletonRows cols={5} rows={4} />
        ) : entries.length === 0 ? (
          <EmptyRow cols={5}>No activity recorded yet.</EmptyRow>
        ) : (
          <tbody>
            {entries.map((e) => (
              <tr key={e.id}>
                <td className="mono">{e.ts.slice(0, 19).replace("T", " ")}</td>
                <td>{e.actor}</td>
                <td>
                  <span className={`pill pill-${e.action.includes("revoke") ? "bad" : "neutral"}`}>
                    {e.action}
                  </span>
                </td>
                <td className="mono">
                  {e.target ? `${e.target.slice(0, 16)}` : ""}
                </td>
                <td className="audit-detail">{e.detail_json ?? ""}</td>
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </section>
  );
}
